import { Navigate, useParams } from "react-router-dom";
import MainLayout from "../layouts/MainLayout";
import Login from "../auth/Login";
import AuthLayout from "../layouts/AuthLayout";
import ForgotPassword from "../auth/ForgotPassword";
import ResetPassword from "../auth/ResetPassword";
import Dashboard from "../pages/Dashboard";
import ProfileOverview from "../pages/ProfileOverview";
import Inbox from "../pages/Inbox";
import Calendar from "../pages/Calendar";
import Chat from "../pages/ClientChat/Chat";
import Vonage from "../pages/Vonage/App";
import SupportCenter from "../pages/SupportCenter";
import History from "../pages/History";
import AllFile from "../pages/AllFiles";
import ClientAccess from "../pages/ClientAccess";
import Users from "../pages/Users";
import Routing from "../pages/Routing";
import ReportHub from "../pages/ReportHub";
import ReportHubExpert from "../pages/ReportHubExpert";
import DigitalTemplate from "../pages/DigitalTemplate";
import NotFound from "../pages/NotFound";
import { useAtomValue } from "jotai";
import { authAtom } from "../states/auth";
import UserForm from "../pages/UserForm";
import DigitalReportCreate from "../pages/ExpertReports/DigitalReportCreate";
import TemplateSelect from "../pages/ExpertReports/TemplateSelect";
import ClientRegister from "../pages/ClientRegister";

const isJsonString = (str) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

const RequireRole = ({ element, allowedRoles = [], fallback = <NotFound /> }) => {
  const auth = useAtomValue(authAtom);
  const role = auth?.user?.roleId;

  if (!auth?.isAuthenticated || !auth?.token) {
    return <Navigate to="/auth/login" replace />;
  }

  if (allowedRoles.includes(role)) {
    return element;
  }

  return fallback;
};


const RequireAuth = ({ element }) => {
  const auth = useAtomValue(authAtom);
  if (typeof auth != null && typeof auth == "object" && auth?.token) {
    return auth && auth?.isAuthenticated ? (
      element
    ) : (
      <Navigate to="/auth/login" replace />
    );
  } else if (
    typeof auth != null &&
    typeof auth == "string" &&
    isJsonString(auth)
  ) {
    const authString = JSON.parse(auth);
    if (authString?.token) {
      return authString && authString?.isAuthenticated ? (
        element
      ) : (
        <Navigate to="/auth/login" replace />
      );
    }
  }
};

const routesConfig1 = [
  {
    path: "/",
    element: <Navigate to="/auth/login" replace />,
  },
  {
    path: "/auth",
    element: <AuthLayout />,
    children: [
      { index: true, element: <Login /> },
      { path: "login", element: <Login /> },
      { path: "forgot-password", element: <ForgotPassword /> },
      { path: "reset-password/:token", element: <ResetPassword /> },
    ],
  },
  {
    path: "reset-password/:token",
    element: <AuthLayout />,
    children: [{ path: "", element: <ResetPassword /> }],
  },
  {
    path: "",
    element: <RequireAuth element={<MainLayout />} />,
    children: [
      { path: "dashboard", element: <Dashboard /> },
      { path: "profile-overview", element: <ProfileOverview /> },
      { path: "inbox", element: <Inbox /> },
      { path: "calendar", element: <Calendar /> },
      { path: "chat", element: <Chat /> },
      { path: "support-center", element: <SupportCenter /> },
      { path: "history", element: <History /> },
      { path: "history/all-files", element: <AllFile /> },
      { path: "client-access", element: <ClientAccess /> },
      { path: "users", element: <Users /> },
      { path: "users/add", element: <UserForm /> },
      { path: "user/edit/:userId", element: <UserForm /> },
      { path: "routing", element: <Routing /> },
      { path: "report-hub", element: <ReportHub /> },
      { path: "report-hub/digital-template", element: <DigitalTemplate /> },
      { path: "report-hub/field-template", element: <DigitalTemplate /> },
      { path: "*", element: <NotFound /> },
    ],
  },
];

const routesConfig = [
  {
    path: "/",
    element: <Navigate to="/auth/login" replace />,
  },
  {
    path: "/auth",
    element: <AuthLayout />,
    children: [
      { index: true, element: <Login /> },
      { path: "login", element: <Login /> },
      { path: "forgot-password", element: <ForgotPassword /> },
      { path: "reset-password/:token", element: <ResetPassword /> },
    ],
  },
  {
    path: "/connect",
    element: <Vonage />,
  },
  {
    path: "",
    element: <RequireAuth element={<MainLayout />} />,
    children: [
      {
        path: "dashboard",
        element: <RequireRole element={<Dashboard />} allowedRoles={[2]} />,
      },
      {
        path: "profile-overview",
        element: (
          <RequireRole element={<ProfileOverview />} allowedRoles={[2, 3]} />
        ),
      },
      {
        path: "users",
        element: <RequireRole element={<Users />} allowedRoles={[2]} />,
      },
      {
        path: "users/add",
        element: <RequireRole element={<UserForm />} allowedRoles={[2]} />,
      },
      {
        path: "user/edit/:userId",
        element: <RequireRole element={<UserForm />} allowedRoles={[2]} />,
      },
      {
        path: "client-access",
        element: (
          <RequireRole element={<ClientAccess />} allowedRoles={[2, 3]} />
        ),
      },
      {
        path: "inbox",
        element: <RequireRole element={<Inbox />} allowedRoles={[3]} />,
      },
      {
        path: "calendar",
        element: <RequireRole element={<Calendar />} allowedRoles={[3]} />,
      },
      {
        path: "chat",
        element: <RequireRole element={<Chat />} allowedRoles={[3]} />,
      },
      {
        path: "support-center",
        element: <RequireRole element={<SupportCenter />} allowedRoles={[2]} />,
      },
      {
        path: "history",
        element: <RequireRole element={<History />} allowedRoles={[3, 2]} />,
      },
      {
        path: "history/all-files",
        element: <RequireRole element={<AllFile />} allowedRoles={[3]} />,
      },
      {
        path: "routing",
        element: <RequireRole element={<Routing />} allowedRoles={[2]} />,
      },
      {
        path: "report-hub",
        element: (
          <RequireRole
            element={<ReportHub />}
            allowedRoles={[2]}
            fallback={<Navigate to="/report-hub/list" replace />}
          />
        ),
      },
      {
        path: "report-hub/list",
        element: (
          <RequireRole element={<ReportHubExpert />} allowedRoles={[2, 3]} />
        ),
      },
      {
        path: "report-hub/edit/:reportId",
        element: (
          <RequireRole element={<DigitalTemplate />} allowedRoles={[2, 3]} />
        ),
      },
      {
        path: "report-hub/create/:templateType",
        element: (
          <RequireRole
            element={
              (() => {
                // Lazy inline logic using a closure
                const TemplateChooser = () => {
                  const { templateType } = useParams();
                  if (templateType === "field") {
                    return <TemplateSelect />;
                  } else if (templateType === "digital") {
                    return <DigitalReportCreate />;
                  } else {
                    return <Navigate to="/report-hub" replace />;
                  }
                };
                return <TemplateChooser />;
              })()
            }
            allowedRoles={[2, 3]}
          />
        ),
      },
      // {
      //   path: "report-hub/create/:templateType",
      //   element: (
      //     <RequireRole
      //       element={<DigitalReportCreate />}
      //       allowedRoles={[2, 3]}
      //     />
      //   ),
      // },
      {
        path: "report-hub/create/:templateType/:conversationId",
        element: (
          <RequireRole
            element={<TemplateSelect />}
            allowedRoles={[2, 3]}
          />
        ),
      },
      {
        path: "connect1",
        element: <RequireRole element={<Vonage />} allowedRoles={[2, 3]} />,
      },
      {
        path: "report-hub/digital-template",
        element: (
          <RequireRole element={<DigitalTemplate />} allowedRoles={[2, 3]} />
        ),
      },
      // {
      //   path: "report-hub/create/:templateType/:conversationId/:templateId",
      //   element: (
      //     <RequireRole element={<DigitalTemplate />} allowedRoles={[2, 3]} />
      //   ),
      // },
      {
        path: "report-hub/create/field/:templateId",
        element: (
          <RequireRole
            element={<DigitalTemplate />}
            allowedRoles={[2, 3]}
          />
        ),
      },
      {
        path: "report-hub/create/:templateType/:conversationId/:templateId",
        element: (
          <RequireRole
            element={
              (() => {
                // Lazy inline logic using a closure
                const TemplateChooser = () => {
                  const { templateType } = useParams();
                  return templateType === "digital"
                  ? <DigitalTemplate />
                  : <Navigate to="/report-hub" replace />;
                };
                return <TemplateChooser />;
              })()
            }
            allowedRoles={[2, 3]}
          />
        ),
      },
      {
        path: "report-hub/digital-template/:templateId",
        element: (
          <RequireRole element={<DigitalTemplate />} allowedRoles={[2, 3]} />
        ),
      },
      {
        path: "report-hub/field-template",
        element: (
          <RequireRole element={<DigitalTemplate />} allowedRoles={[2, 3]} />
        ),
      },
      {
        path: "report-hub/field-template/:templateId",
        element: (
          <RequireRole element={<DigitalTemplate />} allowedRoles={[2, 3]} />
        ),
      },
      {
        path: "client-register",
        element: (
          <RequireRole element={<ClientRegister />} allowedRoles={[2, 3]} />
        ),
      },
      {
        path: "*",
        element: <NotFound />,
      },
    ],
  },
];
export default routesConfig;
